import { put } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { getLogger } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import { commonSelectors, createQcsThunks, createResourceEntityAdapter } from '../core/resourceSliceUtils';
import { invoiceItemsActions } from '../invoice-items';
import type { InvoiceItemType } from '../invoice-items/invoiceItemTypes';
import billableInvoiceItemsApi from './billableInvoiceItemsApi';
import {
  BILLABLE_INVOICE_ITEMS_REDUCER_KEY,
  type BillableInvoiceItemType,
  type BillableInvoiceItemsState,
} from './billableInvoiceItemsTypes';

// Define the initial state using that type
const initialState: BillableInvoiceItemsState = {
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};

const log = getLogger('app-components/flux/billableInvoiceItems/billableInvoiceItemsSlice.ts');

const entityAdapter = createResourceEntityAdapter<BillableInvoiceItemType>();
const entitySelectors = entityAdapter.getSelectors();

type billableInvoiceAction = {
  type: string;
  payload: BillableInvoiceItemType[];
};

const slice = createAppSlice({
  name: BILLABLE_INVOICE_ITEMS_REDUCER_KEY,
  initialState: entityAdapter.getInitialState(initialState),
  reducers: (create) => ({
    ...createQcsThunks({
      api: billableInvoiceItemsApi,
      create,
      adapter: entityAdapter,
      log,
      refetch(dispatch) {
        dispatch(billableInvoiceItemsActions.fetch());
      },
    }),
    /**
     * This is used for when you are creating a billable item but might not have made a global invoice item yet.
     * It will create the global one if there is not a itemId yet.
     **/
    createWithGlobalItem: create.asyncThunk(
      async (item: BillableInvoiceItemType & { clientId: string }, thunkApi): Promise<InvoiceItemType> => {
        let itemId = item.itemId;
        if (!itemId) {
          const parent = await thunkApi
            .dispatch(
              invoiceItemsActions.create({
                ...item,
                clientId: uuid(),
              }),
            )
            .unwrap();
          itemId = parent.id;
        }
        return await thunkApi
          .dispatch(
            slice.actions.create({
              ...item,
              itemId,
            }),
          )
          .unwrap();
      },
    ),
    bumpNudgeBillableItems: create.reducer((state, action: billableInvoiceAction) => {
      const removeArr: string[] = [];
      const addArr: BillableInvoiceItemType[] = [];

      action.payload?.forEach((item) => {
        if (item.isDeleted && item.id) {
          removeArr.push(item.id);
        } else {
          addArr.push(item);
        }
      });

      if (action.payload) {
        if (removeArr.length > 0) {
          entityAdapter.removeMany(state, removeArr);
        }
        if (addArr.length > 0) {
          entityAdapter.setMany(state, addArr);
        }
      }
    }),
  }),
  selectors: {
    ...entitySelectors,
    ...commonSelectors,
  },
});

export { slice as billableInvoiceItemsSlice };
export const billableInvoiceItemsSelectors = slice.selectors;
export const billableInvoiceItemsActions = slice.actions;

export function* billableItemsDumpBump(resources: BillableInvoiceItemType[]) {
  yield put(billableInvoiceItemsActions.bumpNudgeBillableItems(resources));
}

export function* billableItemsNudgeBump() {
  yield put(billableInvoiceItemsActions.fetch());
}

export default slice.reducer;
